import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';

import { FilePond, registerPlugin } from 'react-filepond';

import saveAs from 'file-saver';

import CancelButton from '../../components/psc/lib/CancelButton'
import SaveButton from '../../components/psc/lib/SaveButton'
import DeleteButton from '../../components/psc/lib/DeleteButton'
import ErrorMessage from '../../components/psc/lib/ErrorMessage'
import SingleSelect from '../../components/psc/lib/SingleSelect'
import TemplateBulkSelectModal from './TemplateBulkSelectModal'

import useCadmObject from '../../hooks/useCadmObject'
import useGncHistory from '../../hooks/useGncHistory'
import useQmsDocumentStatuses from '../../hooks/useQmsDocumentStatuses'
import useQaDocumentStatuses from '../../hooks/useQaDocumentStatuses'
import useQaRecordStatuses from '../../hooks/useQaRecordStatuses'

const Template = () => {
    let pond = useRef(null)

    let { objID } = useParams()
    const isNew = objID === "new" ? true : false
    objID = parseInt(objID,10)
    const objectUrl = '/v0/templates' + (isNew ? '' : '/' + objID)

    const {
        goBack,
    } = useGncHistory()

    const {
        reloadCadmObjects,
    } = useCadmObject()
    
    const { reloadQmsDocumentStatuses } = useQmsDocumentStatuses()
    const { reloadQaDocumentStatuses } = useQaDocumentStatuses()
    const { reloadQaRecordStatuses } = useQaRecordStatuses()
    
    // Fields
    const [usages, setUsages] = useState([])

    // Read-only
    const [id, setId] = useState(isNew ? null : objID)
    const [filename, setFilename] = useState('')
    const [filetype, setFiletype] = useState('')
    const [usageObjects, setUsageObjects] = useState([])
    const [isUsed, setIsUsed] = useState(false)
    
    // Submit
    const [submitActive, setSubmitActive] = useState(false)
    const [error, setError] = useState(null)
    const [showBulkAddModal, setShowBulkAddModal] = useState(false)

    useEffect(() => {
        if ( ! isNew) {
            fetchObject()
        } else {
            fetchAttributes()
        }
    }, [isNew])

    const fetchAttributes = () => {
        fetch('/v0/templates/attributes', { credentials: 'same-origin' })
            .then(function(response) {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error(response.statusText)
                }
            })
            .then(function(json) {
                if(json.error) {
                    throw new Error(json.error)
                }
                setUsageObjects(json.result.usage_objects)
            })
            .catch(function(error) {
                console.log(error)
            })
    }
    
    const fetchObject = () => {
        fetch(objectUrl, { credentials: 'same-origin' })
            .then(function(response) {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error(response.statusText)
                }
            })
            .then(function(json) {
                if(json.error) {
                    throw new Error(json.error)
                }

                fromBackend(json.result)
            })
            .catch(function(error) {
                console.log(error)
            })
    }

    const fromBackend = (obj) => {
        obj.usages.map(function (u) {
            u.table_id = u.object_type + '_' + u.object_id
        })


        setFilename(obj.filename)
        setFiletype(obj.filetype)
        setUsages(obj.usages)
        setUsageObjects(obj.usage_objects)
        setIsUsed(obj.is_used)
    }

    const toBackend = () => {
        return {
            usages: usages
        }
    }

    const validateBeforeSubmit = () => {
        let errors = []

        if( isNew && pond.current.getFile() === null ) {
            // new entry needs a file
            errors.push('Template File is required')
        }

        if ( errors.length > 0 ) {
            setError(errors)
            return false
        }

        return true
    }

    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setError([ props.message ])
    }
    
    const handleSubmit = (event) => {
        if( event ) {
            event.preventDefault()
        }
        if( submitActive || !validateBeforeSubmit() ) {
            return null
        }

        setSubmitActive(true)

        return {
            objType: 'templates',
            objID: objID,
            data: toBackend(),
            navigateOverride: function() {
            },
            successCallback: function(res) {
                reloadCadmObjects()
                reloadQmsDocumentStatuses()
                reloadQaDocumentStatuses()
                reloadQaRecordStatuses()
                if (isNew) {
                    // Update the FilePond server URL with the new template ID
                    const newTemplateId = res.result.id;
                    setId(newTemplateId)
                }
                if( pond.current.getFile() === null ) {
                    goBack()
                }
                
                pond.current.processFile().then(() => {
                    console.log("File processed successfully");
                    goBack()
                }).catch((error) => {
                    console.error("Error processing file:", error);
                });
            },
        }
    }

    const viewTemplate = (res) => {
        let content = atob(res.result.contents)
        let bytes = new Uint8Array(content.length);
        for (let i = 0; i < bytes.length; i++) {
            bytes[i] = content.charCodeAt(i);
        }

        let blob = new Blob([bytes], { type: filetype })
        let url = URL.createObjectURL(blob);

        let name = filename
        saveAs(blob, name);
    }

    const getTemplateContents = () => {
        fetch('/v0/templates/' + id + '/contents', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        })
        .then(response => response.json())
        .then(viewTemplate)
        .catch(error => console.error('Error:', error));
    }

    const updateUsage = (field, member, event) => {
        console.log('updateUsage', field, member, event)
        const value = event.value;

        let update = usages.filter(function (u) {
            if(u.table_id === member.table_id) {
                u[field] = value
            }
            return true;
        });
        setUsages(update)
    }

    const addUsage = () => {
        let newUsage = [{ object_type: '', object_id: 0, table_id: ''+new Date().getTime() }]
        setUsages(usages.concat(newUsage))
    }

    const bulkAddUsage = (usagesToAdd, selectedItem) => {
        const updates = usagesToAdd
            .map(option => ({ object_id: option.id, object_type: selectedItem, table_id: `${option.id}_${new Date().getTime()}` }))
        setUsages([...usages, ...updates])
    }

    const removeUsage = (member) => {
        let update = usages.filter(function (m) {
            if(m.table_id === member.table_id) { return false }
            return true;
        });
        setUsages(update)
    }

    const renderUsages = () => {
        let res = []
        // If I delete the options in the copy, usageObjects is affected. Which is not cool, it's used below for optionIdOptions
        const objectTypeOptions = JSON.parse(JSON.stringify(usageObjects))
        objectTypeOptions.map(o => delete o.options)

        usages.map(function(obj) {
            let object = usageObjects.find(function (o) { return o.name === obj.object_type })
            let objectIdOptions = object && object.options ? object.options : []

            res.push(
                <tr key={obj.table_id}>
                    <td className={(obj.error) ? 'error' : ''}>
                        <SingleSelect
                            name="object_type"
                            value={obj.object_type}
                            onChange={(event) => updateUsage('object_type', obj, event)}
                            options={objectTypeOptions}
                        />
                    </td>
                    <td>
                        <SingleSelect
                            name="object_id"
                            value={obj.object_id}
                            onChange={(event) => updateUsage('object_id', obj, event)}
                            options={objectIdOptions}
                        />
                    </td>
                    <td>
                        <button type="button" className="btn btn-xs btn-warning" onClick={() => removeUsage(obj)}>Remove</button>
                    </td>
                </tr>
            )
        })

        return (
            <div className="form-group">
                <label className="col-sm-2 control-label">Use Template for</label>
                <div className="col-sm-8">
                    <table className="table table-striped table-bordered table-hover dataTable" >
                        <thead>
                            <tr>
                                <th>Configured Item</th>
                                <th>Type</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tfoot>
                            <tr>
                                <th>Configured Item</th>
                                <th>Type</th>
                                <th>Action</th>
                            </tr>
                        </tfoot>
                        <tbody>
                            {res}
                            <tr>
                                <td></td>
                                <td></td>
                                <td>
                                    <button type="button" className="btn btn-xs btn-primary" onClick={() => addUsage()}>Add</button>
                                    &nbsp;
                                    <button type="button" className="btn btn-xs btn-light" onClick={() => setShowBulkAddModal(true)}>Bulk add</button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <TemplateBulkSelectModal items={usageObjects} bulkAdd={bulkAddUsage} show={showBulkAddModal} onHide={() => setShowBulkAddModal(false)}/>
            </div>
        )
    }

    let viewFile = (<div></div>)
    let hasTemplate = false

    if ( ! isNew ) {
        hasTemplate = true
        viewFile = (
            <div className="form-group">
                <label className="col-sm-2 control-label">View File</label>
                <div className="col-sm-8" style={{paddingTop: '7px'}}>
                    <a onClick={getTemplateContents}>{filename}</a>
                </div>
            </div>
        );
    }

    let requiredIndicator = (<label className="form-required-star">&nbsp;*</label>);

    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h2>Template: {filename || '<New>'}</h2>
                        </div>
                        <div className="ibox-content">
                            <form className="form-horizontal" onSubmit={(e) => handleSubmit(e)}>

                                {viewFile}

                                <div className="form-group" key="tu1">
                                    <label className="col-sm-2 control-label">Upload a New File
                                        {!hasTemplate && requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <FilePond
                                            ref={pond}
                                            instantUpload={false}
                                            maxFileSize="10MB"
                                            server={{
                                                process: {
                                                    url: '/v0/templates/' + id + '/contents',
                                                },
                                            }}
                                            onaddfile={(error,file)=> {
                                                setFilename(file.filename)
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className="hr-line-dashed"></div>

                                {renderUsages()}

                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <ErrorMessage
                                        error={error}
                                    />

                                    <div className="col-sm-10 col-sm-offset-2">
                                        <CancelButton />
                                        
                                        <SaveButton
                                            buttonVariant='primary'
                                            visible={true}
                                            spinnerActive={submitActive}
                                            label='Save Changes'
                                            onClick={handleSubmit}
                                            onError={handleSubmitError}
                                        />

                                        <DeleteButton
                                           isHidden={isNew}
                                           url={objectUrl}
                                           onError={handleSubmitError}
                                        />

                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Template
